import { DataTexture, FloatType, NearestFilter, RedFormat, ClampToEdgeWrapping } from 'three';
import { WORLD, METERS_PER_TEXEL } from '../config';
import type { HeightJob, HeightResult } from './heightWorker';
import HeightWorker from './heightWorker?worker';

/**
 * Výšková mapa světa držená jednou v paměti a sdílená mezi GPU a CPU.
 *
 * GPU: DataTexture (R32F), ze které vertex shader terénu čte přes texelFetch.
 *      Díky texelFetch + ruční bilineární interpolaci nepotřebujeme filtrování
 *      float textur (které není všude podporované) a neztrácíme přesnost.
 * CPU: stejný Float32Array pro dotazy na výšku (kolize, spawn, kamera).
 *
 * 1025 x 1025 x 4 B = 4,2 MB. Generuje se paralelně ve workerech, aby se
 * hlavní vlákno vůbec nezaseklo.
 */
export class HeightField {
  readonly res = WORLD.heightRes;
  readonly worldSize = WORLD.size;
  readonly data: Float32Array;
  readonly texture: DataTexture;

  private constructor(data: Float32Array) {
    this.data = data;
    this.texture = new DataTexture(data, this.res, this.res, RedFormat, FloatType);
    this.texture.magFilter = NearestFilter;
    this.texture.minFilter = NearestFilter;
    this.texture.wrapS = ClampToEdgeWrapping;
    this.texture.wrapT = ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;
  }

  /** Vygeneruje výškovou mapu paralelně; onProgress dostává 0..1. */
  static async generate(onProgress?: (t: number) => void): Promise<HeightField> {
    const res = WORLD.heightRes;
    const data = new Float32Array(res * res);

    const workerCount = Math.max(1, Math.min(8, navigator.hardwareConcurrency ?? 4));
    const rowsPerWorker = Math.ceil(res / workerCount);

    let done = 0;
    const jobs: Promise<void>[] = [];

    for (let w = 0; w < workerCount; w++) {
      const rowStart = w * rowsPerWorker;
      const rowEnd = Math.min(res, rowStart + rowsPerWorker);
      if (rowStart >= rowEnd) break;

      jobs.push(
        new Promise<void>((resolve, reject) => {
          const worker = new HeightWorker();
          worker.onmessage = (e: MessageEvent<HeightResult>) => {
            data.set(e.data.data, e.data.rowStart * res);
            done++;
            onProgress?.(done / workerCount);
            worker.terminate();
            resolve();
          };
          worker.onerror = (err) => {
            worker.terminate();
            reject(err);
          };
          const job: HeightJob = { seed: WORLD.seed, worldSize: WORLD.size, res, rowStart, rowEnd };
          worker.postMessage(job);
        }),
      );
    }

    await Promise.all(jobs);
    return new HeightField(data);
  }

  /** Výška v metrech pro světové (x, z), bilineárně interpolovaná. Stejná matematika jako v shaderu. */
  heightAt(x: number, z: number): number {
    const half = this.worldSize * 0.5;
    const tx = (x + half) / METERS_PER_TEXEL;
    const tz = (z + half) / METERS_PER_TEXEL;

    const max = this.res - 1;
    const x0 = Math.min(max, Math.max(0, Math.floor(tx)));
    const z0 = Math.min(max, Math.max(0, Math.floor(tz)));
    const x1 = Math.min(max, x0 + 1);
    const z1 = Math.min(max, z0 + 1);
    const fx = Math.min(1, Math.max(0, tx - x0));
    const fz = Math.min(1, Math.max(0, tz - z0));

    const d = this.data;
    const h00 = d[z0 * this.res + x0];
    const h10 = d[z0 * this.res + x1];
    const h01 = d[z1 * this.res + x0];
    const h11 = d[z1 * this.res + x1];

    return (h00 + (h10 - h00) * fx) * (1 - fz) + (h01 + (h11 - h01) * fx) * fz;
  }

  /** Normála terénu ve světových souřadnicích (centrální diference). */
  normalAt(x: number, z: number, out: [number, number, number] = [0, 1, 0]): [number, number, number] {
    const e = METERS_PER_TEXEL;
    const hl = this.heightAt(x - e, z);
    const hr = this.heightAt(x + e, z);
    const hd = this.heightAt(x, z - e);
    const hu = this.heightAt(x, z + e);
    const nx = hl - hr;
    const ny = 2 * e;
    const nz = hd - hu;
    const len = Math.hypot(nx, ny, nz) || 1;
    out[0] = nx / len;
    out[1] = ny / len;
    out[2] = nz / len;
    return out;
  }

  /**
   * Min/max výška v obdélníku — potřeba pro přesné bounding boxy chunků.
   * Bez toho by frustum culling musel počítat s celým výškovým rozsahem světa.
   */
  minMaxIn(x0: number, z0: number, x1: number, z1: number): [number, number] {
    const half = this.worldSize * 0.5;
    const max = this.res - 1;
    const c0 = Math.min(max, Math.max(0, Math.floor((x0 + half) / METERS_PER_TEXEL)));
    const c1 = Math.min(max, Math.max(0, Math.ceil((x1 + half) / METERS_PER_TEXEL)));
    const r0 = Math.min(max, Math.max(0, Math.floor((z0 + half) / METERS_PER_TEXEL)));
    const r1 = Math.min(max, Math.max(0, Math.ceil((z1 + half) / METERS_PER_TEXEL)));

    let lo = Infinity;
    let hi = -Infinity;
    for (let r = r0; r <= r1; r++) {
      const base = r * this.res;
      for (let c = c0; c <= c1; c++) {
        const h = this.data[base + c];
        if (h < lo) lo = h;
        if (h > hi) hi = h;
      }
    }
    return [lo, hi];
  }

  dispose(): void {
    this.texture.dispose();
  }
}
